import { useState } from 'react';

import { useNavigate, useParams } from 'react-router';

import { PermissionLevel } from '@cfa/pathway-fe-shared';
import { SystemPlan } from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';
import useSystemPlans from '@/hooks/useSystemPlans';

import { useUserPermissionLevel } from '@/utils/auth-check';
import { cn } from '@/utils/tailwind';

import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import { PathwayModal } from '@/components/popups/PathwayModal';
import UnauthorizedModal from '@/components/popups/UnauthorizedModal';

import { routes } from '@/constants';

import { useAddReadOnlyPlan } from '../hooks/useAddReadOnlyPlan/useAddReadOnlyPlan';

import CreateOrCopyPlanModalStep from './Tab/Modals/CreateOrCopyPlanModalStep';

import PageNotFound404 from '@/routes/404/404';
import useGetManagePlansData from '@/routes/TrainingPlans/hooks/useGetManagePlansData';
import PreviewTemplateModalStep from '@/routes/TrainingPlans/ManagePlans/Tab/Modals/PreviewTemplateModalStep';

/**
 * AddReadOnlyPlanPage
 *
 * Route component for `/plans/add/:planId` that handles direct links to plan templates.
 * - Shows UnauthorizedModal if user lacks Leader/Operator/Admin/Stakeholder permissions
 * - Shows 404 if plan not found in system plans
 * - Readonly templates: PreviewTemplateModalStep with add/subscribe flow
 * - Editable templates: PreviewTemplateModalStep → CreateOrCopyPlanModalStep (name + clone) flow
 * - Handles compliance plan confirmation via EnableCompliancePlanModal
 */
const AddReadOnlyPlanPage: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const {
    data: auth,
    isFetching: isFetchingAuth,
    isError: isAuthError,
  } = useAuth();

  const isAtLeastLeader = useUserPermissionLevel(
    PermissionLevel.atLeast.LEADER,
  );

  const isAuthLoaded = (!isFetchingAuth && !!auth?.userId) || isAuthError;

  const { isFetchingPlans } = useGetManagePlansData();

  const {
    systemPlans,
    isFetchingSystemPlans,
    isPendingSystemPlans,
    systemPlansError,
  } = useSystemPlans();

  const handleClose = () => {
    navigate(routes.plans.managedPlans);
  };

  const [showPreview, setShowPreview] = useState(true);
  const [showCopyPlan, setShowCopyPlan] = useState(false);
  const [showInvalidNameError, setShowInvalidNameError] = useState(false);

  const {
    subscribeReadonlyPlan,
    isPending,
    complianceModal,
    complianceModalIsOpen,
    onComplianceModalClose,
  } = useAddReadOnlyPlan({ onCancel: handleClose });

  const handleAdd = (template: SystemPlan) => {
    const isCompliance =
      template.category === 'compliance' && template.readonly;
    if (isCompliance) {
      setShowPreview(false);
    }
    subscribeReadonlyPlan(template);
  };

  const plan = systemPlans.find(p => p.id === planId);

  const handleViewProgress = () => {
    if (planId) {
      navigate(routes.plans.managePlan({ planId }));
    }
  };

  const handleCopy = (_id: string) => {
    setShowPreview(false);
    setShowCopyPlan(true);
  };

  const handleCopyCancel = () => {
    setShowInvalidNameError(false);
    setShowCopyPlan(false);
    setShowPreview(true);
  };

  if (!isAuthLoaded) {
    return <LoadingOverlay isOpen />;
  }

  if (!isAtLeastLeader) {
    return <UnauthorizedModal isOpen />;
  }

  const isLoading =
    isFetchingSystemPlans ||
    isPendingSystemPlans ||
    isFetchingPlans ||
    (systemPlans.length === 0 && !systemPlansError);

  if (isLoading) {
    return <LoadingOverlay isOpen />;
  }

  if (systemPlansError || !plan) {
    return <PageNotFound404 />;
  }

  const step = complianceModalIsOpen
    ? 'compliance'
    : showCopyPlan
      ? showInvalidNameError
        ? 'invalidName'
        : 'copy'
      : 'preview';

  return (
    <PathwayModal
      isOpen={showPreview || showCopyPlan || complianceModalIsOpen}
      onClose={
        complianceModalIsOpen
          ? onComplianceModalClose
          : showInvalidNameError
            ? () => setShowInvalidNameError(false)
            : handleClose
      }
      stepKey={step}
      className={cn(
        (step === 'copy' || step === 'invalidName') && '2xl:max-w-[610px]!',
      )}
    >
      <PathwayModal.Step stepKey="compliance">
        {complianceModal}
      </PathwayModal.Step>
      <PathwayModal.Step stepKey={showCopyPlan ? step : 'copy'}>
        <CreateOrCopyPlanModalStep
          copiedTemplateId={planId}
          onCancel={handleCopyCancel}
          setShowInvalidNameError={setShowInvalidNameError}
          showInvalidNameError={showInvalidNameError}
        />
      </PathwayModal.Step>
      <PathwayModal.Step stepKey="preview">
        <PreviewTemplateModalStep
          onCopy={handleCopy}
          onTemplateBackButtonClick={handleClose}
          plan={plan}
          onAdd={plan.readonly ? handleAdd : undefined}
          isLoading={isPending}
          onViewProgress={plan.readonly ? handleViewProgress : undefined}
        />
      </PathwayModal.Step>
    </PathwayModal>
  );
};

export default AddReadOnlyPlanPage;
